/**
 * useReconciliation Hook
 * Fetches reconciliation data from the backend
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/core/api/client';

export interface ReconciliationSummary {
  totalInvoiced: number;
  totalReceived: number;
  totalCredited: number;
  netOutstanding: number;
  invoiceCount: number;
  paidCount: number;
  sentCount: number;
  draftCount: number;
  unmatchedPaymentCount: number;
  unmatchedPaymentAmount: number;
}

export interface ReconciliationInvoice {
  id: number;
  invoice_number: string | null;
  status: string;
  billing_name: string;
  total_htva: number;
  total_tvac: number;
  invoice_date: string;
  structured_communication: string | null;
  total_credited: number;
  net_amount: number;
  total_paid: number;
  payment_count: number;
  reconciliation_status: 'FULLY_PAID' | 'PARTIALLY_PAID' | 'UNPAID';
  linked_payments: Array<{
    payment_id: number;
    amount: number;
    date: string;
    debtor_name: string;
  }> | null;
  affaire_ids: string[] | null;
}

export interface UnmatchedPayment {
  payment_id: number;
  date: string;
  debtor_name: string;
  amount: number;
  affaire_id: string;
  amount_allocated: number;
}

export interface ReconciliationData {
  summary: ReconciliationSummary;
  invoices: ReconciliationInvoice[];
  unmatchedPayments: UnmatchedPayment[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface ReconciliationFilters {
  dateFrom?: string;
  dateTo?: string;
  status?: 'FULLY_PAID' | 'PARTIALLY_PAID' | 'UNPAID' | '';
  search?: string;
  page?: number;
}

export const useReconciliation = (filters: ReconciliationFilters) => {
  return useQuery<ReconciliationData>({
    queryKey: ['reconciliation', filters],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      if (filters.status) params.status = filters.status;
      if (filters.search) params.search = filters.search;
      if (filters.page) params.page = String(filters.page);

      // apiClient interceptor already unwraps response.data,
      // so result is { success, data } directly
      const result = await apiClient.get<{ success: boolean; data: ReconciliationData }>('/api/billing/reconciliation', { params });
      return (result as any)?.data || result;
    },
    staleTime: 30_000,
  });
};
